import React, { useMemo } from "react";
import { create } from "zustand";
import { EditorContext, useCodebaseStore } from "./codebaseStore";
import { PRDDesignSuggestions } from "@/components/AI/PRD/PRD";
import { useAiActionStore, useCurrentConvo } from "./aiActionStore";
import { toast } from "sonner";
import { parseMessageContentBlocks } from "@/components/AI/chat/utils/messages";
import { Message } from "./supabaseStore";
import {
  getAiMessageFromErrors,
  useJoinedErrors,
} from "@/components/Canvas/hooks/useStoryboardErrors";
import { UploadedFile } from "@/utils/types";
import { AI_AGENT } from "./aiActionStore";
import { useActivityDiffs } from "@/hooks/useActivityDiffs";
import useActivityStore from "./activityStore";
import useLayoutStore from "./layoutStore";

export const FULL_CODEBASE_CONSTANT = "___FULL_CODEBASE_DESCRIPTION___";

export interface Suggestion {
  short_title: string;
  detailed_description: string;
}

export interface ChatContextCodeSnippet {
  filename: string;
  startLine: number;
  startCol: number;
  endLine: number;
  endCol: number;
  contents: string;
  editorContext: EditorContext;

  engSpecPrId?: string;
  engSpecCommitId?: string;
  engSpecFileDiffId?: string;
}

export interface ManualAssistantMessage {
  id: string;
  text?: string;

  progressItems?: {
    title: string;
    succeeded?: boolean;
    failed?: boolean;
    loading?: boolean;
  }[];

  afterText?: string;
}

export interface SetSendChatMessageParams {
  sendChatMessage: string;
  sendChatMessageFiles?: UploadedFile[];
  refreshAfterResponse?: boolean;
  includeErrorsIfAny?: boolean;
  isFreePrompt?: boolean;
  isFixWithAi?: boolean;
  resetToggles?: "all" | "reasoning_and_search" | null;
  additionalInstructions?: string;
  agentChoice?: AI_AGENT;
  criticalFiles?: string[];
}

export interface AiContextStore {
  allFiles: string[];
  fileTree: string;
  setAllFiles: (files: string[]) => void;
  setFileTree: (fileTree: string) => void;

  // Eng Spec
  engSpecOpenedFileId: string;
  engSpecOpenedFileIsCommand: boolean;
  setEngSpecOpenedFile: (fileId: string, isCommand: boolean) => void;

  resetAiContextStore: () => void;

  generatingStoryboardIds: Set<string>;
  setGeneratingStoryboardIds: (storyboardIds: Set<string>) => void;

  aiChatEngSpecOpen: boolean;
  aiChatEngSpecMessageId: string;
  aiChatEngSpecBlockIndex: number;
  openAiChatEngSpec: (blockIndex: number, messageId: string) => void;
  closeAiChatEngSpec: () => void;

  // Code Diff Panel (for applyFileEditsTool results)
  codeDiffPanelOpen: boolean;
  codeDiffActivityId: string | null;
  openCodeDiffPanel: (activityId: string) => void;
  closeCodeDiffPanel: () => void;

  aiChatSuggestions: Suggestion[];
  setAiChatSuggestions: (suggestions: Suggestion[]) => void;

  // Trigger the chat from anywhere in the app
  sendChatMessage: string;
  sendChatMessageFiles?: UploadedFile[];
  refreshAfterResponse: boolean; // If set to true, refreshes all storyboards after a response
  includeErrorsIfAny: boolean; // If set to true, includes errors in the chat message
  isFreePrompt: boolean; // If set to true, the prompt is free
  additionalInstructions?: string; // Hidden instructions for AI
  agentChoice?: AI_AGENT; // AI agent choice for this message
  criticalFiles?: string[]; // Critical files for context
  setSendChatMessage: (params: SetSendChatMessageParams) => boolean;
  resetToggles: (resetToggles?: "all" | "reasoning_and_search" | null) => void;

  // For tracking fix with AI messages
  numFixWithAiMessages: number;
  setNumFixWithAiMessages: (num: number) => void;

  // Show an extra assistant message on the end of the conversation
  // If set cannot change conversation or use chat until cleared
  tempAssistantMessage: ManualAssistantMessage | null;
  setTempAssistantMessage: (message: ManualAssistantMessage | null) => void;

  manualMessageToAdd: ManualAssistantMessage | null;
  setAddManualMessage: (message: ManualAssistantMessage | null) => void;

  chatOnlyMode: boolean;
  setChatOnlyMode: (enabled: boolean) => void;

  setUseSearch: (enabled: boolean) => void;
  useSearch: boolean;

  setUseReasoning: (enabled: boolean) => void;
  useReasoning: boolean;
}

export const useAiContextStore = create<AiContextStore>((set, get) => ({
  allFiles: [],
  fileTree: "",

  setAllFiles: (files: string[]) => set({ allFiles: files }),
  setFileTree: (fileTree: string) => set({ fileTree: fileTree }),

  // @ts-expect-error TS2322
  engSpecOpenedFileId: null,
  engSpecOpenedFileIsCommand: false,
  setEngSpecOpenedFile: (fileId: string, isCommand: boolean) => {
    set({ engSpecOpenedFileId: fileId, engSpecOpenedFileIsCommand: isCommand });
  },

  chatOnlyMode: false,
  useReasoning: false,
  useSearch: false,

  resetAiContextStore: () => {
    set({
      allFiles: [],
      fileTree: "",
      aiChatEngSpecOpen: false,
      // @ts-expect-error TS2322
      generatingStoryboardIds: null,
      // @ts-expect-error TS2322
      aiChatEngSpecBlockIndex: null,
      // @ts-expect-error TS2322
      aiChatEngSpecMessageId: null,
      // @ts-expect-error TS2322
      engSpecOpenedFileId: null,
      sendChatMessage: "",
      sendChatMessageFiles: undefined,
      refreshAfterResponse: false,
      includeErrorsIfAny: false,
      isFreePrompt: false,
      additionalInstructions: undefined,
      agentChoice: undefined,
      criticalFiles: undefined,
      tempAssistantMessage: null,
      manualMessageToAdd: null,
      numFixWithAiMessages: 0,
      chatOnlyMode: false,
      useReasoning: false,
      useSearch: false,
    });
  },

  // @ts-expect-error TS2322
  generatingStoryboardIds: null,
  setGeneratingStoryboardIds: (generating: Set<string>) => {
    set({ generatingStoryboardIds: generating });
  },

  // @ts-expect-error TS2322
  aiChatEngSpecBlockIndex: null,
  aiChatEngSpecOpen: false,
  // @ts-expect-error TS2322
  aiChatEngSpecMessageId: null,
  openAiChatEngSpec: (blockIndex: number, messageId: string) => {
    set({
      aiChatEngSpecBlockIndex: blockIndex,
      aiChatEngSpecOpen: true,
      aiChatEngSpecMessageId: messageId,
    });
  },
  closeAiChatEngSpec: () => {
    set({
      // @ts-expect-error TS2322
      aiChatEngSpecBlockIndex: null,
      aiChatEngSpecOpen: false,
      // @ts-expect-error TS2322
      aiChatEngSpecMessageId: null,
    });
  },

  codeDiffPanelOpen: false,
  codeDiffActivityId: null,
  openCodeDiffPanel: (activityId: string) => {
    set({
      codeDiffPanelOpen: true,
      codeDiffActivityId: activityId,
    });
  },
  closeCodeDiffPanel: () => {
    set({
      codeDiffPanelOpen: false,
      codeDiffActivityId: null,
    });
  },

  // @ts-expect-error TS2322
  aiChatSuggestions: null,
  setAiChatSuggestions: (suggestions: Suggestion[]) => {
    set({ aiChatSuggestions: suggestions });
  },

  sendChatMessage: "",
  sendChatMessageFiles: undefined,
  refreshAfterResponse: false,
  includeErrorsIfAny: false,
  isFreePrompt: false,
  additionalInstructions: undefined,
  agentChoice: undefined,
  criticalFiles: undefined,
  setSendChatMessage: ({
    sendChatMessage,
    sendChatMessageFiles,
    refreshAfterResponse,
    includeErrorsIfAny,
    isFreePrompt,
    isFixWithAi,
    resetToggles,
    additionalInstructions,
    agentChoice,
    criticalFiles,
  }: SetSendChatMessageParams) => {
    const tempAssistantMessage = get().tempAssistantMessage;
    const { chatStreamingMessages } = useAiActionStore.getState();

    const isChatLoading = calculateIsChatLoading({
      chatStreamingMessages: chatStreamingMessages,
      tempAssistantMessage,
    });

    if (isChatLoading) {
      toast.info("Please wait for AI to finish the previous request");
      return false;
    }

    let numFixWithAiMessages = get().numFixWithAiMessages;
    if (isFixWithAi) {
      numFixWithAiMessages += 1;
    } else if (sendChatMessage) {
      // Clear if sending a non-fix with AI message
      // Otherwise keep the same as this function is also used
      // to clear the send chat message
      numFixWithAiMessages = 0;
    }

    set({
      sendChatMessage,
      sendChatMessageFiles,
      refreshAfterResponse,
      includeErrorsIfAny: includeErrorsIfAny || false,
      isFreePrompt: isFreePrompt || false,
      additionalInstructions,
      agentChoice,
      criticalFiles,
      numFixWithAiMessages,
    });

    if (resetToggles === "all") {
      set({
        chatOnlyMode: false,
        useReasoning: false,
        useSearch: false,
      });
    } else if (resetToggles === "reasoning_and_search") {
      set({
        useReasoning: false,
        useSearch: false,
      });
    }

    return true;
  },
  resetToggles: (resetToggles?: "all" | "reasoning_and_search" | null) => {
    if (resetToggles === "all") {
      set({
        chatOnlyMode: false,
        useReasoning: false,
        useSearch: false,
      });
    } else if (resetToggles === "reasoning_and_search") {
      set({
        useReasoning: false,
        useSearch: false,
      });
    }
  },

  numFixWithAiMessages: 0,
  setNumFixWithAiMessages: (num: number) => {
    set({ numFixWithAiMessages: num });
  },

  tempAssistantMessage: null,
  setTempAssistantMessage: (message: ManualAssistantMessage) => {
    set({ tempAssistantMessage: message });
  },

  manualMessageToAdd: null,
  setAddManualMessage: (message: ManualAssistantMessage) => {
    set({ manualMessageToAdd: message });
  },

  setChatOnlyMode: (enabled: boolean) => {
    set({ chatOnlyMode: enabled });
  },

  setUseReasoning: (enabled: boolean) => {
    set({ useReasoning: enabled });
  },

  setUseSearch: (enabled: boolean) => {
    set({ useSearch: enabled });
  },
}));

export const useCurrentMessagesBeingShown = () => {
  const chatStreamingMessages = useAiActionStore(
    (state) => state.chatStreamingMessages,
  );

  const isChatLoading = useIsChatLoading();

  // Manually adding an assistant message
  // Temporary - while streaming in
  const tempAssistantMessage = useAiContextStore(
    (state) => state.tempAssistantMessage,
  );

  const { messages } = useCurrentConvo();

  const allErrors = useJoinedErrors();

  const withTempAssistantMessage = useMemo(() => {
    const baseMessages = chatStreamingMessages || messages || [];

    if (!tempAssistantMessage) return baseMessages;

    let fullTextContent = tempAssistantMessage?.text || "";
    if (tempAssistantMessage?.progressItems?.length) {
      fullTextContent +=
        '\n<action-progress>\n{"items":' +
        JSON.stringify(tempAssistantMessage?.progressItems) +
        "}\n</action-progress>";
    }
    if (tempAssistantMessage?.afterText) {
      fullTextContent += `\n${tempAssistantMessage?.afterText}`;
    }
    const newMessage = {
      ...tempAssistantMessage,
      content: fullTextContent,
    };
    return [...baseMessages, newMessage as any];
  }, [chatStreamingMessages, messages, tempAssistantMessage]);

  const withErrors = useMemo(() => {
    if (!allErrors?.length || isChatLoading) return withTempAssistantMessage;

    const withErrorsMessage = getAiMessageFromErrors(allErrors);

    return [...withTempAssistantMessage, withErrorsMessage];
  }, [withTempAssistantMessage, allErrors, isChatLoading]);

  return withErrors;
};

export const getOpenedAiChatEngSpec = (
  messages: Message[],
  aiChatEngSpecBlockIndex: number,
  aiChatEngSpecMessageId: string,
) => {
  const messageIdx = messages?.findIndex(
    (m) => m.id === aiChatEngSpecMessageId,
  );

  if (
    !messages?.length ||
    aiChatEngSpecBlockIndex === null ||
    messageIdx === undefined
  )
    return null;

  const message = messages[messageIdx];

  const res = parseMessageContentBlocks(message);
  const block = res[aiChatEngSpecBlockIndex];

  if (block?.type === "aiChanges") {
    const aiChanges = block.aiChanges;

    // Only the last segment in last message is being streamed
    // Last segment is always guaranteed to be a content block
    const isStreaming =
      aiChatEngSpecBlockIndex === res.length - 1 &&
      messageIdx === messages.length - 1;

    return {
      commits: [
        {
          id: `change-from-chat-${message.id}`,
          feature: "Change made from chat",
          description: "",
          changes: aiChanges?.changes?.map((change, idx) => ({
            ...change,
            change: {
              ...(change?.change || {}),
              streaming: isStreaming,
            },
          })),
        },
      ],
    };
  }

  return null;
};

export const useOpenedAiChatEngSpec = () => {
  const messages = useCurrentMessagesBeingShown();

  const aiChatEngSpecMessageId = useAiContextStore(
    (state) => state.aiChatEngSpecMessageId,
  );

  const aiChatEngSpecBlockIndex = useAiContextStore(
    (state) => state.aiChatEngSpecBlockIndex,
  );

  return useMemo(() => {
    return getOpenedAiChatEngSpec(
      messages,
      aiChatEngSpecBlockIndex,
      aiChatEngSpecMessageId,
    );
  }, [messages, aiChatEngSpecBlockIndex, aiChatEngSpecMessageId]);
};

export const useCodeDiffData = () => {
  const codeDiffActivityId = useAiContextStore(
    (state) => state.codeDiffActivityId,
  );

  const { calculatedDiffs, diffErrors, calculatedDiffsActivityId } =
    useActivityDiffs(codeDiffActivityId || "");

  return {
    calculatedDiffs,
    diffErrors,
    calculatedDiffsActivityId,
    activityId: codeDiffActivityId,
  };
};

export const useCodeDiffSetup = () => {
  const codeDiffPanelOpen = useAiContextStore(
    (state) => state.codeDiffPanelOpen,
  );
  const codeDiffActivityId = useAiContextStore(
    (state) => state.codeDiffActivityId,
  );
  const { calculatedDiffs, diffErrors, calculatedDiffsActivityId } =
    useCodeDiffData();
  const setEngSpecFiles = useCodebaseStore((state) => state.setEngSpecFiles);
  const expandLeft = useLayoutStore((state) => state.expandLeft);

  React.useEffect(() => {
    if (!codeDiffPanelOpen) {
      setEngSpecFiles({ files: [] });
      return;
    }

    if (!codeDiffActivityId) {
      setEngSpecFiles({ files: [] });
      return;
    }

    if (calculatedDiffsActivityId !== codeDiffActivityId) {
      setEngSpecFiles({ files: [] });
      return;
    }

    if (diffErrors?.length) {
      toast.error(`Unable to show diff: ${diffErrors[0].message}`);
      setEngSpecFiles({ files: [] });
      return;
    }

    if (!calculatedDiffs?.length) {
      setEngSpecFiles({ files: [] });
      return;
    }

    expandLeft();

    const files = calculatedDiffs.map((diff) => ({
      id: `code-diff-${diff.filePath}`,
      filename: diff.filePath,
      keySuffix: codeDiffActivityId,
      calculatedDiff: diff,
      hardOpened: true,
      context: EditorContext.EngSpec,
    }));

    setEngSpecFiles({ files });
  }, [
    codeDiffPanelOpen,
    codeDiffActivityId,
    calculatedDiffs,
    calculatedDiffsActivityId,
    diffErrors,
    setEngSpecFiles,
    expandLeft,
  ]);
};

/**
 * Opened file but with the most up-to-date streamed information
 */
export const useEngSpecOpenedFile = () => {
  const engSpecOpenedFileId = useAiContextStore(
    (state) => state.engSpecOpenedFileId,
  );

  const codeDiffPanelOpen = useAiContextStore(
    (state) => state.codeDiffPanelOpen,
  );
  const codeDiffActivityId = useAiContextStore(
    (state) => state.codeDiffActivityId,
  );

  const aiChatEngSpec = useOpenedAiChatEngSpec();
  const { calculatedDiffs } = useCodeDiffData();

  return useMemo(() => {
    // If code diff panel is open, provide code diff data
    if (codeDiffPanelOpen && calculatedDiffs?.length) {
      // Use the first calculated diff (typically one file per activity)
      const diff = calculatedDiffs[0];
      const fileCommit = {
        id: `code-diff-${codeDiffActivityId}`,
        path: diff.filePath,
        change: {
          originalContents: diff.old,
          newFullFileContent: diff.new,
        },
      };

      return {
        file: fileCommit,
        commit: {
          id: `code-diff-commit-${codeDiffActivityId}`,
          feature: "Change made from chat",
          description: "",
          changes: [fileCommit],
        },
        source: "chat",
      };
    }

    // Default EngSpec behavior
    let foundFile, foundCommit;
    aiChatEngSpec?.commits?.forEach((commit) => {
      if (foundFile) return;

      commit.changes?.forEach((file) => {
        if (foundFile) return;

        if (file.id === engSpecOpenedFileId) {
          foundFile = file;
          foundCommit = commit;
        }
      });
    });

    return {
      file: foundFile,
      commit: foundCommit,
      source: "chat",
    };
  }, [aiChatEngSpec, engSpecOpenedFileId, codeDiffPanelOpen]);
};

const calculateIsChatLoading = ({
  chatStreamingMessages,
  tempAssistantMessage,
}: {
  chatStreamingMessages: Message[] | null;
  tempAssistantMessage: ManualAssistantMessage | null;
}) => {
  return chatStreamingMessages != null || tempAssistantMessage != null;
};

export const useIsChatLoading = () => {
  const chatStreamingMessages = useAiActionStore(
    (state) => state.chatStreamingMessages,
  );

  const tempAssistantMessage = useAiContextStore(
    (state) => state.tempAssistantMessage,
  );

  return calculateIsChatLoading({
    chatStreamingMessages: chatStreamingMessages,
    tempAssistantMessage,
  });
};
